<script>
import { useConsult } from '@site/composables/useConsult'
import { novaCapabilities, novaModules, novaPackages } from '@site/data/nova'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'NovaProductView',
  components: { AppIcon },
  data() {
    const { open } = useConsult()
    return {
      open,
      novaCapabilities,
      novaModules,
      novaPackages,
    }
  },
}
</script>

<template>
  <div class="nova-page">
    <!-- Hero -->
    <section class="nova-hero">
      <div class="nova-hero__glow nova-hero__glow--1"></div>
      <div class="nova-hero__glow nova-hero__glow--2"></div>
      <div class="container nova-hero__inner">
        <span class="nova-hero__flag"><AppIcon name="rocket" :size="15" /> 旗舰版</span>
        <h1>航富通 Nova · 更快、更省心、先人一步</h1>
        <p>覆盖与航富通相同的门店生意，Nova 以更快的性能、更便捷的操作和更快迭代，让经营更轻松</p>
        <div class="nova-hero__cta">
          <button class="btn btn--primary" @click="open">咨询 Nova</button>
          <router-link to="/compare" class="btn btn--outline">与航富通对比</router-link>
        </div>
      </div>
    </section>

    <!-- 定位 banner -->
    <section class="section section--white">
      <div class="container">
        <div class="nova-banner reveal">
          <span class="nova-banner__icon"><AppIcon name="rocket" :size="28" /></span>
          <div>
            <h2>旗舰版 · 为追求更好体验的你而生</h2>
            <p>同样的门店生意，Nova 让你更快、更省心、更快用上新功能</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 差异能力 -->
    <section class="section section--light">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">旗舰优势</span>
          <h2>Nova 的快与便捷，你用了就知道</h2>
          <p>在航富通全部能力之上，Nova 再进阶旗舰体验</p>
        </div>
        <div class="grid grid--3">
          <article v-for="(c, i) in novaCapabilities" :key="i" class="card nova-card reveal">
            <div class="icon-badge"><AppIcon :name="c.icon" /></div>
            <h3>{{ c.title }}</h3>
            <p>{{ c.desc }}</p>
            <span v-if="c.isPending" class="nova-card__pending">建议补充 · 待产品确认</span>
          </article>
        </div>
      </div>
    </section>

    <!-- 功能全景 -->
    <section class="section section--dark">
      <div class="container">
        <div class="section-head section-head--light reveal">
          <span class="section-eyebrow">旗舰体验</span>
          <h2>以更优体验为核心的旗舰能力</h2>
        </div>
        <div class="grid grid--4">
          <div v-for="(m, i) in novaModules" :key="i" class="nova-module reveal">
            <h3>{{ m.title }}</h3>
            <p>{{ m.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 客户案例 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">适用场景</span>
          <h2>追求更快体验的审美门店，都在用</h2>
        </div>
        <div class="grid grid--3">
          <div v-for="s in ['追求流畅报表的门店', '重视效率开单的门店', '抢先体验新功能的门店']" :key="s" class="card reveal">
            <div class="icon-badge"><AppIcon name="store" /></div>
            <h3 style="margin: 14px 0 6px">{{ s }}</h3>
            <p style="color:#6E6E73">用 Nova 的快与便捷，让门店经营更从容高效。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 硬件套餐 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">硬件套餐</span>
          <h2>选择适合你门店的 Nova 硬件组合</h2>
          <p>官方标配一口价，不同硬件配置满足多样经营场景</p>
        </div>
        <div class="table-scroll reveal">
          <table class="pkg-table">
            <thead>
              <tr>
                <th>套餐类型</th>
                <th>官方标配</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in novaPackages" :key="pkg.hardware">
                <td class="pkg-table__hw">{{ pkg.hardware }}</td>
                <td>
                  <div class="pkg-table__price">{{ pkg.standardPrice }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="nova-cta">
      <div class="container">
        <h2>与我们的顾问聊聊 Nova</h2>
        <p>现在咨询，获取面向你门店的旗舰体验方案与演示</p>
        <button class="btn btn--primary" @click="open">咨询 Nova</button>
      </div>
    </section>
  </div>
</template>


<style lang="scss" scoped>
.nova-page { padding-top: 72px; }

.nova-hero {
  position: relative;
  background: linear-gradient(135deg, #241638 0%, #8B5CF6 55%, #EC4899 100%);
  color: $color-white;
  text-align: center;
  padding: 128px 0 104px;
  overflow: hidden;

  &__glow {
    position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; opacity: 0.5;
    &--1 { width: 560px; height: 400px; top: -80px; left: 50%; transform: translateX(-50%); background: radial-gradient(circle, rgba(236,72,153,0.45), transparent 65%); }
    &--2 { width: 360px; height: 360px; bottom: -100px; right: -60px; background: radial-gradient(circle, rgba(168,85,247,0.5), transparent 60%); }
  }

  &__inner { position: relative; z-index: 1; }

  &__flag {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,255,255,0.14);
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: $radius-full;
    padding: 6px 18px;
    font-weight: $weight-semibold;
    font-size: 13px;
    margin-bottom: 24px;
    backdrop-filter: blur(6px);
  }

  h1 { color: $color-white; font-size: $font-hero; font-weight: $weight-black; margin-bottom: 22px; letter-spacing: -0.025em; }
  p { font-size: 19px; color: rgba(255,255,255,0.78); max-width: 640px; margin: 0 auto 36px; }
  &__cta { display: flex; justify-content: center; gap: 14px; }
}

.nova-banner {
  display: flex;
  align-items: center;
  gap: 24px;
  background: linear-gradient(135deg, #FBE9F6 0%, #F1E9FF 100%);
  color: $color-text;
  border: 1px solid rgba(236, 72, 153, 0.20);
  border-radius: $radius-lg;
  padding: 44px;
  box-shadow: 0 8px 26px rgba(236, 72, 153, 0.10);

  &__icon {
    width: 64px; height: 64px; border-radius: 16px;
    background: linear-gradient(135deg, #8B5CF6, #EC4899);
    color: $color-white;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 6px 16px rgba(139, 92, 246, 0.22);
  }

  h2 { color: $color-text; font-size: 30px; font-weight: $weight-black; margin-bottom: 8px; letter-spacing: -0.02em; }
  p { color: $color-text-muted; }
}

.nova-card {
  h3 { margin: 18px 0 8px; font-size: $font-card; font-weight: $weight-bold; }
  p { color: $color-text-muted; font-size: 15px; line-height: 1.6; }
  &__pending { display: inline-block; margin-top: 12px; font-size: 12px; color: $color-accent; background: $color-accent-soft; padding: 3px 12px; border-radius: $radius-full; font-weight: $weight-medium; }
}

.nova-module {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: $radius-md;
  padding: 30px;
  text-align: center;
  transition: border-color $motion-base $ease-out;
  h3 { color: $color-white; font-size: 18px; font-weight: $weight-semibold; margin-bottom: 8px; }
  p { color: rgba(255,255,255,0.62); font-size: 14px; }
  &:hover { border-color: rgba(255,255,255,0.18); }
}

.nova-cta {
  background: $gradient-brand;
  color: $color-white;
  text-align: center;
  padding: $space-section 0;
  h2 { color: $color-white; font-size: $font-section; font-weight: $weight-black; margin-bottom: 14px; letter-spacing: -0.02em; }
  p { color: rgba(255,255,255,0.85); margin-bottom: 30px; }
}

.pkg-table {
  width: 100%;
  border-collapse: collapse;
  background: $color-bg-white;
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: $shadow-sm;
  border: 1px solid rgba(29,29,31,0.05);

  th, td { padding: 18px 22px; text-align: center; border-bottom: 1px solid rgba(29,29,31,0.05); font-size: 15px; }
  th { background: $color-bg-light; color: $color-primary; font-weight: $weight-semibold; }
  &__hw { font-weight: $weight-bold; text-align: left; color: $color-text; }
  &__price { font-weight: $weight-bold; color: $color-primary; }
}

@media (max-width: $bp-sm) {
  .nova-hero h1 { font-size: 36px; }
  .nova-banner { flex-direction: column; text-align: center; padding: 30px; }
  .nova-cta h2 { font-size: 32px; }
}
</style>



// WEBPACK FOOTER //
// src/site/views/NovaProductView.vue